import React, { useEffect, useMemo, useState } from "react";
import {
  Menu,
  Package,
  ShoppingCart,
  Wrench,
  Users,
  TrendingUp,
  RefreshCcw,
  AlertCircle,
  Eye,
  BadgeDollarSign,
  Trophy,
  Percent,
  Target,
  Globe2,
  MousePointerClick,
  SearchCheck,
  BarChart3,
  CalendarDays,
} from "lucide-react";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from "recharts";

import Sidebar from "../../components/Sidebar";
import { API_BASE_URL } from "../../config/api";

const formatMoney = (value) =>
  new Intl.NumberFormat("sq-AL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));


const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const normalizeDateValue = (value) => {
  if (!value) return "";

  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) return "";

  return toDateInputValue(parsed);
};

const formatDateDMY = (value) => {
  const normalized = normalizeDateValue(value);

  if (!normalized) return "";

  const [year, month, day] = normalized.split("-");

  return `${day}/${month}/${year}`;
};

const isDateInsideRange = (value, startDate, endDate) => {
  const normalized = normalizeDateValue(value);

  if (!normalized) return false;

  if (startDate && normalized < startDate) return false;
  if (endDate && normalized > endDate) return false;

  return true;
};

const getItemDateValue = (item) =>
  item?.createdAt ||
  item?.created_at ||
  item?.date ||
  item?.createdDate ||
  item?.updated_at ||
  item?.updatedAt ||
  null;

const AdminAnalysis = () => {
  const now = new Date();
  const today = toDateInputValue(now);

  const firstDayDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    1
  );
  const firstDayOfMonth = toDateInputValue(firstDayDate);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [startDate, setStartDate] = useState(firstDayOfMonth);
  const [endDate, setEndDate] = useState(today);

  const [appliedStartDate, setAppliedStartDate] =
    useState(firstDayOfMonth);
  const [appliedEndDate, setAppliedEndDate] = useState(today);

  const [showDateRange, setShowDateRange] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [stats, setStats] = useState({
    products: 0,
    parts: 0,
    orders: 0,
    repairs: 0,
    users: 0,
    sales: 0,
    salesTotal: 0,
    soldQuantity: 0,
    repairsTotal: 0,
    visitors: 0,

    webVisitors: 0,
    webPageViews: 0,
    topSoldProduct: null,
    topClickedProduct: null,
    topSearchedProduct: null,
    topSearchTerm: null,
    webVisitorsTimeline: [],

    salesByUser: [],
    repairsByUser: [],
    salesTimeline: [],
  });

  const [orders, setOrders] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);

  const token = localStorage.getItem("authToken");

  const authHeaders = {
    Authorization: `Bearer ${token}`,
  };

  const handleSearch = () => {
    setError("");

    if (!startDate || !endDate) {
      setError("Zgjidh datën Nga dhe datën Deri.");
      return;
    }

    if (startDate > endDate) {
      setError("Data Nga nuk mund të jetë pas datës Deri.");
      return;
    }

    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setRefreshKey((value) => value + 1);
    setShowDateRange(false);
  };

  // ===============================
  // SAFE FETCH
  // ===============================
  const fetchJson = async (url) => {
    const response = await fetch(url, {
      headers: authHeaders,
    });

    if (!response.ok) {
      throw new Error(`Gabim gjatë marrjes së të dhënave: ${response.status}`);
    }

    return response.json();
  };

  // ===============================
  // LOAD REAL DATA
  // ===============================
  const loadAnalysisData = async () => {
    setLoading(true);
    setError("");

    try {
      const results = await Promise.allSettled([
        fetchJson(
          `${API_BASE_URL}/api/stats?startDate=${encodeURIComponent(
            appliedStartDate
          )}&endDate=${encodeURIComponent(appliedEndDate)}`
        ),
        fetchJson(`${API_BASE_URL}/api/orders`),
        fetchJson(`${API_BASE_URL}/api/repairs`),
        fetchJson(`${API_BASE_URL}/api/products`),
        fetchJson(`${API_BASE_URL}/api/users`),
      ]);

      const [
        statsResult,
        ordersResult,
        repairsResult,
        productsResult,
        usersResult,
      ] = results;

      if (statsResult.status === "fulfilled") {
        setStats((previous) => ({
          ...previous,
          ...statsResult.value,
        }));
      }

      if (ordersResult.status === "fulfilled") {
        const ordersData = Array.isArray(ordersResult.value)
          ? ordersResult.value
          : ordersResult.value?.orders || [];

        setOrders(
          ordersData.filter((item) =>
            isDateInsideRange(
              getItemDateValue(item),
              appliedStartDate,
              appliedEndDate
            )
          )
        );
      }

      if (repairsResult.status === "fulfilled") {
        const repairsData = Array.isArray(repairsResult.value)
          ? repairsResult.value
          : repairsResult.value?.repairs || [];

        setRepairs(
          repairsData.filter((item) =>
            isDateInsideRange(
              getItemDateValue(item),
              appliedStartDate,
              appliedEndDate
            )
          )
        );
      }

      if (productsResult.status === "fulfilled") {
        const productsData = Array.isArray(productsResult.value)
          ? productsResult.value
          : productsResult.value?.products || [];

        setProducts(
          productsData.filter((item) =>
            isDateInsideRange(
              getItemDateValue(item),
              appliedStartDate,
              appliedEndDate
            )
          )
        );
      }

      if (usersResult.status === "fulfilled") {
        const usersData = Array.isArray(usersResult.value)
          ? usersResult.value
          : usersResult.value?.users || [];

        setUsers(
          usersData.filter((item) =>
            isDateInsideRange(
              getItemDateValue(item),
              appliedStartDate,
              appliedEndDate
            )
          )
        );
      }

      const allFailed = results.every(
        (result) => result.status === "rejected"
      );

      if (allFailed) {
        throw new Error("Nuk u morën të dhënat nga serveri.");
      }
    } catch (err) {
      console.error("Gabim në analizë:", err);
      setError(err.message || "Nuk u ngarkuan të dhënat.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalysisData();
  }, [appliedStartDate, appliedEndDate, refreshKey]);

  // ===============================
  // DATE HELPERS
  // ===============================
  const getItemDate = (item) => {
    const dateValue = getItemDateValue(item);

    if (!dateValue) return null;

    const parsedDate = new Date(dateValue);

    return Number.isNaN(parsedDate.getTime())
      ? null
      : parsedDate;
  };

  // ===============================
  // RANGE REAL DATA
  // ===============================
  const monthlyData = useMemo(() => {
    const map = new Map();

    if (appliedStartDate && appliedEndDate) {
      const cursor = new Date(`${appliedStartDate}T12:00:00`);
      const lastDate = new Date(`${appliedEndDate}T12:00:00`);

      while (cursor <= lastDate) {
        const dateKey = toDateInputValue(cursor);

        map.set(dateKey, {
          key: dateKey,
          date: dateKey,
          label: formatDateDMY(dateKey),
          porosi: 0,
          servise: 0,
          shitje: 0,
        });

        cursor.setDate(cursor.getDate() + 1);
      }
    }

    orders.forEach((order) => {
      const dateKey = normalizeDateValue(
        getItemDateValue(order)
      );

      if (!isDateInsideRange(
        dateKey,
        appliedStartDate,
        appliedEndDate
      )) {
        return;
      }

      const target = map.get(dateKey);

      if (target) {
        target.porosi += 1;
      }
    });

    repairs.forEach((repair) => {
      const dateKey = normalizeDateValue(
        getItemDateValue(repair)
      );

      if (!isDateInsideRange(
        dateKey,
        appliedStartDate,
        appliedEndDate
      )) {
        return;
      }

      const target = map.get(dateKey);

      if (target) {
        target.servise += 1;
      }
    });

    const salesTimeline = Array.isArray(stats.salesTimeline)
      ? stats.salesTimeline
      : [];

    salesTimeline
      .filter((sale) =>
        isDateInsideRange(
          sale?.date ||
            sale?.created_at ||
            sale?.createdAt ||
            sale?.day,
          appliedStartDate,
          appliedEndDate
        )
      )
      .forEach((sale) => {
        const dateKey = normalizeDateValue(
          sale?.date ||
            sale?.created_at ||
            sale?.createdAt ||
            sale?.day
        );

        const target = map.get(dateKey);

        if (!target) return;

        target.shitje += Number(
          sale.sales ||
            sale.sales_count ||
            sale.count ||
            0
        );
      });

    return Array.from(map.values()).sort((a, b) =>
      a.date.localeCompare(b.date)
    );
  }, [
    orders,
    repairs,
    stats.salesTimeline,
    appliedStartDate,
    appliedEndDate,
  ]);

  // ===============================
  // PRODUCT CATEGORY DATA
  // ===============================
  const categoryData = useMemo(() => {
    const categoryMap = {};

    products.forEach((product) => {
      const category =
        product?.category?.trim() || "Pa kategori";

      categoryMap[category] =
        (categoryMap[category] || 0) + 1;
    });

    return Object.entries(categoryMap)
      .map(([name, value]) => ({
        name,
        value,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [products]);

  // ===============================
  // ORDER STATUS
  // ===============================
  const orderStatusData = useMemo(() => {
    const statusLabels = {
      pending: "Në pritje",
      confirmed: "Konfirmuar",
      shipped: "Dërguar",
      cancelled: "Anuluar",
    };

    const statusMap = {};

    orders.forEach((order) => {
      const rawStatus = order?.status || "pending";
      const label =
        statusLabels[rawStatus] || rawStatus;

      statusMap[label] =
        (statusMap[label] || 0) + 1;
    });

    return Object.entries(statusMap).map(
      ([name, value]) => ({
        name,
        value,
      })
    );
  }, [orders]);

  // ===============================
  // RECENT ACTIVITY
  // ===============================
  const recentActivity = useMemo(() => {
    const orderActivities = orders.map((order) => ({
      id: `order-${order.id}`,
      title: "Porosi e re",
      description:
        order?.product?.name ||
        order?.product_name ||
        `Porosia #${order.id}`,
      date: getItemDate(order),
      type: "order",
    }));

    const repairActivities = repairs.map((repair) => ({
      id: `repair-${repair.id}`,
      title: "Servis i regjistruar",
      description:
        repair?.phone_type ||
        repair?.phoneType ||
        repair?.issue_description ||
        `Servisi #${repair.id}`,
      date: getItemDate(repair),
      type: "repair",
    }));

    return [...orderActivities, ...repairActivities]
      .filter((item) => item.date)
      .sort((a, b) => b.date - a.date)
      .slice(0, 6);
  }, [orders, repairs]);

  // ===============================
  // REAL COUNTS
  // ===============================
  const realStats = {
    products:
      products.length || Number(stats.products || 0),

    parts: Number(stats.parts || 0),

    orders:
      orders.length || Number(stats.orders || 0),

    repairs:
      repairs.length || Number(stats.repairs || 0),

    users:
      users.length || Number(stats.users || 0),
  };

  const totalActivities =
    realStats.orders + realStats.repairs;

  const completedOrders = orders.filter(
    (order) =>
      order.status === "confirmed" ||
      order.status === "shipped"
  ).length;

  const completionRate =
    realStats.orders > 0
      ? Math.round(
          (completedOrders / realStats.orders) * 100
        )
      : 0;

  const stockTotal = products.reduce(
    (sum, product) =>
      sum + Number(product.stock || 0),
    0
  );

  const lowStockProducts = products.filter(
    (product) =>
      Number(product.stock || 0) <= 3
  ).length;


  // ===============================
  // SALES / BONUS ANALYSIS
  // ===============================
  const salesByUserData = useMemo(() => {
    const rows = Array.isArray(stats.salesByUser)
      ? stats.salesByUser
      : [];

    const totalRevenue = rows.reduce(
      (sum, item) => sum + Number(item.total || 0),
      0
    );

    return rows
      .map((item) => {
        const salesCount = Number(item.sales_count || 0);
        const quantitySold = Number(item.quantity_sold || 0);
        const revenue = Number(item.total || 0);

        const profit = Number(
          item.profit ??
            item.total_profit ??
            item.sales_profit ??
            0
        );

        const bonus = Number(
          item.bonus ??
            item.total_bonus ??
            item.sales_bonus ??
            0
        );

        return {
          id: item.seller_id,
          name:
            item.seller_name ||
            item.username ||
            "Përdorues i panjohur",
          role: item.seller_role || "user",
          sales: salesCount,
          quantity: quantitySold,
          revenue,
          averageSale:
            salesCount > 0 ? revenue / salesCount : 0,
          contribution:
            totalRevenue > 0
              ? (revenue / totalRevenue) * 100
              : 0,
          profit,
          bonus,
        };
      })
      .sort((a, b) => b.revenue - a.revenue)
      .map((item, index) => ({
        ...item,
        rank: index + 1,
      }));
  }, [stats.salesByUser]);

  const totalSalesRevenue =
    Number(stats.salesTotal || 0) ||
    salesByUserData.reduce(
      (sum, item) => sum + item.revenue,
      0
    );

  const totalSalesCount =
    Number(stats.sales || 0) ||
    salesByUserData.reduce(
      (sum, item) => sum + item.sales,
      0
    );

  const totalSoldQuantity =
    Number(stats.soldQuantity || 0) ||
    salesByUserData.reduce(
      (sum, item) => sum + item.quantity,
      0
    );

  const totalBonuses = salesByUserData.reduce(
    (sum, item) => sum + item.bonus,
    0
  );

  const averageSaleValue =
    totalSalesCount > 0
      ? totalSalesRevenue / totalSalesCount
      : 0;

  const topSeller =
    salesByUserData.length > 0
      ? salesByUserData[0]
      : null;

  // ===============================
  // VISITORS -> SALES
  // ===============================
  const totalVisitors = Number(stats.visitors || 0);

  const visitorSalesRate =
    totalVisitors > 0
      ? (totalSalesCount / totalVisitors) * 100
      : 0;

  const visitorsWithoutSale = Math.max(
    totalVisitors - totalSalesCount,
    0
  );

  // ===============================
  // WEB ANALYTICS
  // ===============================

  const webVisitors = Number(stats.webVisitors || 0);
  const webPageViews = Number(stats.webPageViews || 0);

  const pagesPerVisitor =
    webVisitors > 0
      ? webPageViews / webVisitors
      : 0;

  const topSoldProduct = stats.topSoldProduct || null;
  const topClickedProduct = stats.topClickedProduct || null;
  const topSearchedProduct = stats.topSearchedProduct || null;
  const topSearchTerm = stats.topSearchTerm || null;

  const topSoldQuantity = Number(
    topSoldProduct?.quantity_sold || 0
  );

  const topSoldRevenue = Number(
    topSoldProduct?.revenue || 0
  );

  const topClickedCount = Number(
    topClickedProduct?.clicks || 0
  );

  const topClickedUniqueVisitors = Number(
    topClickedProduct?.unique_visitors || 0
  );

  const topSearchedCount = Number(
    topSearchedProduct?.searches ||
      topSearchTerm?.searches ||
      0
  );

  const topSearchedUniqueVisitors = Number(
    topSearchedProduct?.unique_visitors ||
      topSearchTerm?.unique_visitors ||
      0
  );

  const webVisitorsTimelineData = useMemo(() => {
    const rows = Array.isArray(stats.webVisitorsTimeline)
      ? stats.webVisitorsTimeline
      : [];

    const map = new Map();

    if (appliedStartDate && appliedEndDate) {
      const cursor = new Date(`${appliedStartDate}T12:00:00`);
      const lastDate = new Date(`${appliedEndDate}T12:00:00`);

      while (cursor <= lastDate) {
        const dateKey = toDateInputValue(cursor);

        map.set(dateKey, {
          date: dateKey,
          label: formatDateDMY(dateKey),
          visitors: 0,
          pageViews: 0,
        });

        cursor.setDate(cursor.getDate() + 1);
      }
    }

    rows
      .filter((item) =>
        isDateInsideRange(
          item?.date ||
            item?.day ||
            item?.created_at ||
            item?.createdAt,
          appliedStartDate,
          appliedEndDate
        )
      )
      .forEach((item) => {
        const dateKey = normalizeDateValue(
          item?.date ||
            item?.day ||
            item?.created_at ||
            item?.createdAt
        );

        if (!dateKey) return;

        const current = map.get(dateKey) || {
          date: dateKey,
          label: formatDateDMY(dateKey),
          visitors: 0,
          pageViews: 0,
        };

        current.visitors = Number(
          item.visitors ||
            item.count ||
            0
        );

        current.pageViews = Number(
          item.page_views ||
            item.pageViews ||
            0
        );

        map.set(dateKey, current);
      });

    return Array.from(map.values()).sort((a, b) =>
      a.date.localeCompare(b.date)
    );
  }, [
    stats.webVisitorsTimeline,
    appliedStartDate,
    appliedEndDate,
  ]);

  // ===============================
  // REPAIRS ANALYSIS
  // ===============================
  const repairsByUserData = useMemo(() => {
    const rows = Array.isArray(stats.repairsByUser)
      ? stats.repairsByUser
      : [];

    return rows
      .map((item) => ({
        id:
          item.user_id ??
          item.technician_id ??
          item.id,
        name:
          item.user_name ||
          item.technician_name ||
          item.username ||
          "Përdorues i panjohur",
        repairs: Number(
          item.repairs_count ||
          item.repair_count ||
          item.count ||
          0
        ),
        revenue: Number(
          item.total ||
          item.repairs_total ||
          item.revenue ||
          0
        ),
      }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [stats.repairsByUser]);

  const totalRepairRevenue =
    Number(stats.repairsTotal || 0) ||
    repairsByUserData.reduce(
      (sum, item) => sum + item.revenue,
      0
    );

  const totalBusinessRevenue =
    totalSalesRevenue + totalRepairRevenue;

  const statCards = [
    {
      title: "Produkte",
      value: realStats.products,
      icon: <Package size={24} />,
      subtitle: `${stockTotal} njësi në stok`,
      color: "from-blue-500 to-blue-700",
    },
    {
      title: "Shitje",
      value: totalSalesCount,
      icon: <ShoppingCart size={24} />,
      subtitle: `${totalSoldQuantity} artikuj të shitur`,
      color: "from-indigo-500 to-indigo-700",
    },
    {
      title: "Të ardhura nga shitjet",
      value: `${formatMoney(totalSalesRevenue)} ALL`,
      icon: <BadgeDollarSign size={24} />,
      subtitle: `Mesatare ${formatMoney(averageSaleValue)} ALL / shitje`,
      color: "from-cyan-500 to-cyan-700",
    },
    {
      title: "Bonuse totale",
      value: `${formatMoney(totalBonuses)} ALL`,
      icon: <Trophy size={24} />,
      subtitle: "Sipas bonusit të përcaktuar te produkti/pjesa",
      color: "from-pink-500 to-rose-600",
    },
    {
      title: "Vizitorë",
      value: totalVisitors,
      icon: <Eye size={24} />,
      subtitle: `${visitorSalesRate.toFixed(1)}% u kthyen në shitje`,
      color: "from-sky-500 to-blue-600",
    },
    {
      title: "Servise",
      value: realStats.repairs,
      icon: <Wrench size={24} />,
      subtitle: `${formatMoney(totalRepairRevenue)} ALL të ardhura`,
      color: "from-purple-500 to-purple-700",
    },
    {
      title: "Porosi",
      value: realStats.orders,
      icon: <ShoppingCart size={24} />,
      subtitle: `${completedOrders} të përfunduara`,
      color: "from-orange-500 to-orange-600",
    },
    {
      title: "Të ardhura totale",
      value: `${formatMoney(totalBusinessRevenue)} ALL`,
      icon: <TrendingUp size={24} />,
      subtitle: "Shitje + servise",
      color: "from-emerald-500 to-green-700",
    },
  ];

  const formatActivityDate = (date) => {
    if (!date) return "-";

    return new Intl.DateTimeFormat("sq-AL", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-gray-100 text-gray-800">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* HAMBURGER */}
      <button
        type="button"
        onClick={() => setSidebarOpen(true)}
        className="
          fixed left-4 top-4 z-30
          flex h-11 w-11
          items-center justify-center
          rounded-xl bg-blue-700
          text-white shadow-lg
          transition-all duration-200
          hover:bg-blue-800
          active:scale-95
          lg:hidden
        "
        aria-label="Hap menunë"
      >
        <Menu size={24} />
      </button>

      <main
        className="
          min-h-screen
          min-w-0
          overflow-x-hidden
          px-4 pb-8 pt-20
          sm:px-6
          lg:ml-64
          lg:w-[calc(100%-16rem)]
          lg:overflow-visible
          lg:p-10
        "
      >
        {/* HEADER */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">
              Paneli i analizave
            </h1>

            <p className="mt-1 text-sm text-gray-500 sm:text-base">
              Monitoro performancën e produkteve, porosive,
              serviseve dhe përdoruesve.
            </p>
          </div>

          <div className="relative w-full sm:w-auto">
            <button
              type="button"
              onClick={() => setShowDateRange((value) => !value)}
              className="
                flex min-h-[64px] w-full items-center justify-between gap-4
                rounded-2xl border border-slate-200
                bg-white px-5 py-3
                text-left shadow-sm transition
                hover:border-blue-300 hover:shadow-md
                sm:min-w-[300px]
              "
            >
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                  <CalendarDays size={22} />
                </div>

                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                    Data e sotme
                  </p>

                  <p className="mt-0.5 text-lg font-extrabold text-slate-900">
                    {formatDateDMY(today)}
                  </p>
                </div>
              </div>

              <div className="text-right">
                <p className="text-[11px] font-semibold text-slate-400">
                  Range aktiv
                </p>

                <p className="mt-0.5 text-xs font-bold text-blue-700">
                  {formatDateDMY(appliedStartDate)} -{" "}
                  {formatDateDMY(appliedEndDate)}
                </p>
              </div>
            </button>

            {showDateRange && (
              <div
                className="
                  absolute right-0 top-[74px] z-40
                  w-full min-w-[320px]
                  rounded-2xl border border-slate-200
                  bg-white p-4 shadow-xl
                  sm:w-[430px]
                "
              >
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-extrabold text-slate-900">
                      Zgjidh periudhën
                    </p>

                    <p className="text-xs text-slate-400">
                      Formati: ditë / muaj / vit
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowDateRange(false)}
                    className="rounded-lg px-2 py-1 text-sm font-bold text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    aria-label="Mbyll"
                  >
                    ✕
                  </button>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-500">
                      Nga
                    </label>

                    <label
                      className="
                        relative flex h-12 w-full cursor-pointer items-center
                        rounded-xl border border-slate-200 bg-white px-3
                        text-base font-bold text-slate-700
                        transition hover:border-blue-300
                        focus-within:border-blue-500
                        focus-within:ring-4 focus-within:ring-blue-100
                      "
                    >
                      <span>{formatDateDMY(startDate)}</span>

                      <CalendarDays
                        size={18}
                        className="ml-auto text-blue-600"
                      />

                      <input
                        type="date"
                        value={startDate}
                        max={endDate || today}
                        onChange={(event) =>
                          setStartDate(event.target.value)
                        }
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        aria-label="Data nga"
                      />
                    </label>

                    <p className="mt-1 text-[11px] font-semibold text-slate-400">
                      ditë / muaj / vit
                    </p>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-500">
                      Deri
                    </label>

                    <label
                      className="
                        relative flex h-12 w-full cursor-pointer items-center
                        rounded-xl border border-slate-200 bg-white px-3
                        text-base font-bold text-slate-700
                        transition hover:border-blue-300
                        focus-within:border-blue-500
                        focus-within:ring-4 focus-within:ring-blue-100
                      "
                    >
                      <span>{formatDateDMY(endDate)}</span>

                      <CalendarDays
                        size={18}
                        className="ml-auto text-blue-600"
                      />

                      <input
                        type="date"
                        value={endDate}
                        min={startDate || undefined}
                        max={today}
                        onChange={(event) =>
                          setEndDate(event.target.value)
                        }
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        aria-label="Data deri"
                      />
                    </label>

                    <p className="mt-1 text-[11px] font-semibold text-slate-400">
                      ditë / muaj / vit
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={handleSearch}
                    disabled={loading}
                    className="
                      flex h-12 flex-1 items-center justify-center gap-2
                      rounded-xl bg-blue-700 px-5
                      text-sm font-bold text-white
                      shadow-sm transition
                      hover:bg-blue-800
                      disabled:cursor-not-allowed
                      disabled:opacity-60
                    "
                  >
                    <SearchCheck size={18} />
                    Kërko
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setRefreshKey((value) => value + 1)
                    }
                    disabled={loading}
                    className="
                      flex h-12 w-12 items-center justify-center
                      rounded-xl border border-slate-200
                      bg-white text-slate-600
                      shadow-sm transition hover:bg-slate-50
                      disabled:opacity-60
                    "
                    title="Rifresko"
                    aria-label="Rifresko"
                  >
                    <RefreshCcw
                      size={18}
                      className={loading ? "animate-spin" : ""}
                    />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ERROR */}
        {error && (
          <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle
              size={20}
              className="mt-0.5 shrink-0"
            />

            <span>{error}</span>
          </div>
        )}

        {/* LOADING */}
        {loading && (
          <div className="mb-6 flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
            <RefreshCcw
              size={18}
              className="animate-spin"
            />

            Po ngarkohen të dhënat...
          </div>
        )}

        {/* STAT CARDS */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {statCards.map((item) => (
            <div
              key={item.title}
              className={`
                rounded-2xl
                bg-gradient-to-br
                ${item.color}
                p-5
                text-white
                shadow-lg
                transition-all duration-300
                hover:-translate-y-1
                hover:shadow-xl
              `}
            >
              <div className="flex items-start justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/20">
                  {item.icon}
                </div>

                <TrendingUp size={19} className="text-white/70" />
              </div>

              <div className="mt-5 text-3xl font-bold">
                {item.value}
              </div>

              <p className="mt-1 font-medium">
                {item.title}
              </p>

              <p className="mt-1 text-xs text-white/75">
                {item.subtitle}
              </p>
            </div>
          ))}
        </div>

        {/* CHARTS */}
        <div className="mb-8 grid grid-cols-1 gap-6 xl:grid-cols-2">
          {/* MONTHLY TREND */}
          <section className="min-w-0 rounded-2xl bg-white p-4 shadow-sm sm:p-6">
            <div className="mb-5">
              <h2 className="text-lg font-bold text-gray-900">
                Ecuria sipas range-it
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                Porositë, serviset dhe shitjet sipas periudhës së zgjedhur
              </p>
            </div>

            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={monthlyData}
                  margin={{
                    top: 10,
                    right: 10,
                    left: -20,
                    bottom: 0,
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                  />

                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10 }}
                    minTickGap={28}
                    interval="preserveStartEnd"
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />

                  <Line
                    type="monotone"
                    dataKey="porosi"
                    name="Porosi"
                    stroke="#2563eb"
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />

                  <Line
                    type="monotone"
                    dataKey="servise"
                    name="Servise"
                    stroke="#9333ea"
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />

                  <Line
                    type="monotone"
                    dataKey="shitje"
                    name="Shitje"
                    stroke="#16a34a"
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* CATEGORY CHART */}
          <section className="min-w-0 rounded-2xl bg-white p-4 shadow-sm sm:p-6">
            <div className="mb-5">
              <h2 className="text-lg font-bold text-gray-900">
                Produktet sipas kategorisë
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                Shpërndarja reale e produkteve
              </p>
            </div>

            {categoryData.length > 0 ? (
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={categoryData}
                    margin={{
                      top: 10,
                      right: 10,
                      left: -20,
                      bottom: 20,
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                    />

                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11 }}
                      interval={0}
                      angle={-20}
                      textAnchor="end"
                      height={60}
                    />

                    <YAxis allowDecimals={false} />
                    <Tooltip />

                    <Bar
                      dataKey="value"
                      name="Produkte"
                      fill="#2563eb"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex h-[300px] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-500">
                Nuk ka të dhëna për kategoritë.
              </div>
            )}
          </section>
        </div>


        {/* VISITORS -> SALES REPORT */}
        <section className="mb-8 rounded-2xl bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <Target size={22} />
                </div>

                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    Raporti Vizitorë → Shitje
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Mat sa efektivisht vizitorët po konvertohen në shitje.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-blue-50 px-6 py-3 text-center">
              <p className="text-xs font-bold uppercase tracking-wider text-blue-500">
                Conversion Rate
              </p>
              <p className="mt-1 text-3xl font-extrabold text-blue-700">
                {visitorSalesRate.toFixed(1)}%
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="rounded-2xl border border-blue-100 bg-blue-50/50 p-5">
              <Eye className="text-blue-600" size={22} />
              <p className="mt-4 text-sm text-gray-500">Vizitorë</p>
              <p className="mt-1 text-3xl font-extrabold text-gray-900">
                {totalVisitors}
              </p>
            </div>

            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/50 p-5">
              <ShoppingCart className="text-emerald-600" size={22} />
              <p className="mt-4 text-sm text-gray-500">Shitje</p>
              <p className="mt-1 text-3xl font-extrabold text-gray-900">
                {totalSalesCount}
              </p>
            </div>

            <div className="rounded-2xl border border-orange-100 bg-orange-50/50 p-5">
              <Users className="text-orange-600" size={22} />
              <p className="mt-4 text-sm text-gray-500">Pa shitje</p>
              <p className="mt-1 text-3xl font-extrabold text-gray-900">
                {visitorsWithoutSale}
              </p>
            </div>

            <div className="rounded-2xl border border-violet-100 bg-violet-50/50 p-5">
              <Percent className="text-violet-600" size={22} />
              <p className="mt-4 text-sm text-gray-500">Konvertimi</p>
              <p className="mt-1 text-3xl font-extrabold text-gray-900">
                {visitorSalesRate.toFixed(1)}%
              </p>
            </div>
          </div>

          <div className="mt-6">
            <div className="mb-2 flex justify-between text-sm font-medium text-gray-600">
              <span>Vizitorë që u kthyen në shitje</span>
              <span>{visitorSalesRate.toFixed(1)}%</span>
            </div>

            <div className="h-3 overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-700"
                style={{
                  width: `${Math.min(visitorSalesRate, 100)}%`,
                }}
              />
            </div>
          </div>
        </section>

        {/* WEB ANALYTICS DETAIL */}
        <section className="mb-8 rounded-2xl bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
                  <Globe2 size={22} />
                </div>

                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    Analiza e website-it
                  </h2>

                  <p className="mt-1 text-sm text-gray-500">
                    Vizitorët online, shikimet e faqeve, produktet më të shitura,
                    më të klikuara dhe më të kërkuara.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-violet-50 px-5 py-3 text-center">
              <p className="text-xs font-bold uppercase tracking-wider text-violet-500">
                Faqe / vizitor
              </p>

              <p className="mt-1 text-3xl font-extrabold text-violet-700">
                {pagesPerVisitor.toFixed(2)}
              </p>
            </div>
          </div>

          {/* WEB KPI CARDS */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl border border-violet-100 bg-violet-50/50 p-5">
              <Globe2 className="text-violet-600" size={22} />

              <p className="mt-4 text-sm text-gray-500">
                Vizitorë unikë nga webi
              </p>

              <p className="mt-1 text-3xl font-extrabold text-gray-900">
                {webVisitors}
              </p>

              <p className="mt-2 text-xs text-gray-400">
                Vizitorë të ndryshëm për periudhën e zgjedhur
              </p>
            </div>

            <div className="rounded-2xl border border-sky-100 bg-sky-50/50 p-5">
              <Eye className="text-sky-600" size={22} />

              <p className="mt-4 text-sm text-gray-500">
                Page views
              </p>

              <p className="mt-1 text-3xl font-extrabold text-gray-900">
                {webPageViews}
              </p>

              <p className="mt-2 text-xs text-gray-400">
                Numri total i faqeve të hapura
              </p>
            </div>

            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/50 p-5">
              <TrendingUp className="text-emerald-600" size={22} />

              <p className="mt-4 text-sm text-gray-500">
                Produkti më i shitur
              </p>

              <p className="mt-1 line-clamp-2 text-lg font-extrabold text-gray-900">
                {topSoldProduct?.product_name || "—"}
              </p>

              <p className="mt-2 text-xs font-semibold text-emerald-700">
                {topSoldProduct
                  ? `${topSoldQuantity} copë · ${formatMoney(topSoldRevenue)} ALL`
                  : "Nuk ka ende të dhëna"}
              </p>
            </div>

            <div className="rounded-2xl border border-fuchsia-100 bg-fuchsia-50/50 p-5">
              <MousePointerClick
                className="text-fuchsia-600"
                size={22}
              />

              <p className="mt-4 text-sm text-gray-500">
                Produkti më i klikuar
              </p>

              <p className="mt-1 line-clamp-2 text-lg font-extrabold text-gray-900">
                {topClickedProduct?.product_name || "—"}
              </p>

              <p className="mt-2 text-xs font-semibold text-fuchsia-700">
                {topClickedProduct
                  ? `${topClickedCount} klikime · ${topClickedUniqueVisitors} vizitorë unikë`
                  : "Nuk ka ende të dhëna"}
              </p>
            </div>
          </div>

          {/* SECOND ROW */}
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="rounded-2xl border border-cyan-100 bg-cyan-50/50 p-5">
              <SearchCheck
                className="text-cyan-700"
                size={22}
              />

              <p className="mt-4 text-sm text-gray-500">
                Produkti më i kërkuar
              </p>

              <p className="mt-1 line-clamp-2 text-lg font-extrabold text-gray-900">
                {topSearchedProduct?.product_name ||
                  topSearchTerm?.search_term ||
                  "—"}
              </p>

              <p className="mt-2 text-xs font-semibold text-cyan-700">
                {topSearchedCount > 0
                  ? `${topSearchedCount} kërkime · ${topSearchedUniqueVisitors} vizitorë unikë`
                  : "Nuk ka ende të dhëna"}
              </p>
            </div>

            <div className="rounded-2xl border border-amber-100 bg-amber-50/50 p-5">
              <SearchCheck
                className="text-amber-600"
                size={22}
              />

              <p className="mt-4 text-sm text-gray-500">
                Termi më i kërkuar
              </p>

              <p className="mt-1 break-words text-lg font-extrabold text-gray-900">
                {topSearchTerm?.search_term || "—"}
              </p>

              <p className="mt-2 text-xs font-semibold text-amber-700">
                {topSearchTerm
                  ? `${Number(topSearchTerm.searches || 0)} kërkime`
                  : "Nuk ka ende të dhëna"}
              </p>
            </div>

            <div className="rounded-2xl border border-blue-100 bg-blue-50/50 p-5">
              <BarChart3
                className="text-blue-600"
                size={22}
              />

              <p className="mt-4 text-sm text-gray-500">
                Interes / vizitor
              </p>

              <p className="mt-1 text-3xl font-extrabold text-gray-900">
                {pagesPerVisitor.toFixed(2)}
              </p>

              <p className="mt-2 text-xs text-gray-400">
                Mesatarja e faqeve të hapura për çdo vizitor unik
              </p>
            </div>
          </div>

          {/* WEB TRAFFIC CHART */}
          <div className="mt-6 rounded-2xl border border-gray-100 bg-gray-50/50 p-4 sm:p-5">
            <div className="mb-4">
              <h3 className="font-bold text-gray-900">
                Ecuria e trafikut web
              </h3>

              <p className="mt-1 text-sm text-gray-500">
                Vizitorët unikë dhe page views sipas periudhës së zgjedhur.
              </p>
            </div>

            {webVisitorsTimelineData.length === 0 ? (
              <div className="flex h-[280px] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white text-sm text-gray-500">
                Nuk ka ende të dhëna të trafikut web.
              </div>
            ) : (
              <div className="h-[320px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={webVisitorsTimelineData}
                    margin={{
                      top: 10,
                      right: 10,
                      left: -20,
                      bottom: 0,
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                    />

                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10 }}
                      minTickGap={28}
                      interval="preserveStartEnd"
                    />

                    <YAxis allowDecimals={false} />

                    <Tooltip />
                    <Legend />

                    <Line
                      type="monotone"
                      dataKey="visitors"
                      name="Vizitorë unikë"
                      stroke="#7c3aed"
                      strokeWidth={3}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />

                    <Line
                      type="monotone"
                      dataKey="pageViews"
                      name="Page views"
                      stroke="#0ea5e9"
                      strokeWidth={3}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </section>

        {/* USER SALES + BONUS DETAIL */}
        <section className="mb-8 rounded-2xl bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Performanca dhe bonuset sipas përdoruesit
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Bonusi merret nga fitimi dhe përqindja e përcaktuar për produktin/pjesën e shitur.
              </p>
            </div>

            <div className="rounded-xl bg-pink-50 px-4 py-3 text-right">
              <p className="text-xs font-semibold text-pink-500">
                Bonus total
              </p>
              <p className="text-xl font-extrabold text-pink-700">
                {formatMoney(totalBonuses)} ALL
              </p>
            </div>
          </div>

          {salesByUserData.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-12 text-center text-gray-500">
              Nuk ka të dhëna shitjesh sipas përdoruesit.
            </div>
          ) : (
            <>
              <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 p-5 text-white">
                  <div className="flex items-center justify-between">
                    <Trophy size={24} />
                    <span className="text-xs font-bold uppercase">
                      Top Seller
                    </span>
                  </div>
                  <p className="mt-5 text-xl font-extrabold">
                    {topSeller?.name || "-"}
                  </p>
                  <p className="mt-1 text-sm text-white/80">
                    {formatMoney(topSeller?.revenue || 0)} ALL xhiro
                  </p>
                </div>

                <div className="rounded-2xl bg-slate-50 p-5">
                  <p className="text-sm text-gray-500">
                    Mesatarja për shitje
                  </p>
                  <p className="mt-2 text-2xl font-extrabold text-gray-900">
                    {formatMoney(averageSaleValue)} ALL
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    Xhiro totale / numër shitjesh
                  </p>
                </div>

                <div className="rounded-2xl bg-slate-50 p-5">
                  <p className="text-sm text-gray-500">
                    Xhiro totale e shitjeve
                  </p>
                  <p className="mt-2 text-2xl font-extrabold text-gray-900">
                    {formatMoney(totalSalesRevenue)} ALL
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    {totalSalesCount} shitje · {totalSoldQuantity} artikuj
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="min-w-[1180px] w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                      <th className="px-4 py-3">#</th>
                      <th className="px-4 py-3">Përdoruesi</th>
                      <th className="px-4 py-3">Roli</th>
                      <th className="px-4 py-3">Shitje</th>
                      <th className="px-4 py-3">Artikuj</th>
                      <th className="px-4 py-3">Xhiro</th>
                      <th className="px-4 py-3">Fitimi</th>
                      <th className="px-4 py-3">Mesatare / shitje</th>
                      <th className="px-4 py-3">Kontribut</th>
                      <th className="px-4 py-3">Bonus</th>
                    </tr>
                  </thead>

                  <tbody>
                    {salesByUserData.map((item, index) => (
                      <tr
                        key={item.id ?? `${item.name}-${index}`}
                        className="border-t border-gray-100 text-sm hover:bg-gray-50/70"
                      >
                        <td className="px-4 py-4 font-bold text-gray-500">
                          {item.rank === 1 ? "🥇" : item.rank}
                        </td>

                        <td className="px-4 py-4 font-semibold text-gray-900">
                          {item.name}
                        </td>

                        <td className="px-4 py-4">
                          <span
                            className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${
                              item.role === "admin"
                                ? "bg-red-100 text-red-700"
                                : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            {item.role === "admin" ? "Admin" : "User"}
                          </span>
                        </td>

                        <td className="px-4 py-4 font-bold">
                          {item.sales}
                        </td>

                        <td className="px-4 py-4">
                          {item.quantity}
                        </td>

                        <td className="px-4 py-4 font-bold text-blue-700">
                          {formatMoney(item.revenue)} ALL
                        </td>

                        <td className="px-4 py-4 font-bold text-emerald-700">
                          {formatMoney(item.profit)} ALL
                        </td>

                        <td className="px-4 py-4">
                          {formatMoney(item.averageSale)} ALL
                        </td>

                        <td className="px-4 py-4">
                          <div className="min-w-[120px]">
                            <div className="mb-1 flex justify-between text-xs">
                              <span>{item.contribution.toFixed(1)}%</span>
                            </div>
                            <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                              <div
                                className="h-full rounded-full bg-blue-500"
                                style={{
                                  width: `${Math.min(item.contribution, 100)}%`,
                                }}
                              />
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-4">
                          <span className="rounded-lg bg-pink-50 px-3 py-2 font-extrabold text-pink-700">
                            {formatMoney(item.bonus)} ALL
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>

        {/* REPAIR PERFORMANCE */}
        <section className="mb-8 rounded-2xl bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-5">
            <h2 className="text-lg font-bold text-gray-900">
              Analiza e serviseve sipas përdoruesit
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Numri i serviseve dhe të ardhurat që ka gjeneruar secili përdorues/teknik.
            </p>
          </div>

          {repairsByUserData.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center text-gray-500">
              Nuk ka të dhëna servisi sipas përdoruesit.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {repairsByUserData.map((item, index) => (
                <div
                  key={item.id ?? `${item.name}-${index}`}
                  className="rounded-2xl border border-gray-100 bg-gray-50/70 p-5"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-gray-900">
                        {item.name}
                      </p>
                      <p className="mt-1 text-xs text-gray-400">
                        {item.repairs} servise
                      </p>
                    </div>

                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-100 text-purple-700">
                      <Wrench size={19} />
                    </div>
                  </div>

                  <div className="mt-5 border-t border-gray-200 pt-4">
                    <p className="text-xs text-gray-500">
                      Të ardhura nga serviset
                    </p>
                    <p className="mt-1 text-xl font-extrabold text-purple-700">
                      {formatMoney(item.revenue)} ALL
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* LOWER GRID */}
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          {/* RECENT ACTIVITY */}
          <section className="rounded-2xl bg-white p-5 shadow-sm sm:p-6 xl:col-span-2">
            <div className="mb-5">
              <h2 className="text-lg font-bold text-gray-900">
                Aktiviteti i fundit
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                Porositë dhe serviset më të fundit
              </p>
            </div>

            {recentActivity.length > 0 ? (
              <div className="space-y-3">
                {recentActivity.map((activity) => (
                  <div
                    key={activity.id}
                    className="
                      flex flex-col gap-3
                      rounded-xl border border-gray-100
                      p-4 transition
                      hover:border-blue-200
                      hover:bg-blue-50/30
                      sm:flex-row
                      sm:items-center
                      sm:justify-between
                    "
                  >
                    <div className="flex min-w-0 items-start gap-3">
                      <div
                        className={`
                          flex h-10 w-10 shrink-0
                          items-center justify-center
                          rounded-xl
                          ${
                            activity.type === "order"
                              ? "bg-orange-50 text-orange-600"
                              : "bg-purple-50 text-purple-600"
                          }
                        `}
                      >
                        {activity.type === "order" ? (
                          <ShoppingCart size={19} />
                        ) : (
                          <Wrench size={19} />
                        )}
                      </div>

                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900">
                          {activity.title}
                        </h3>

                        <p className="mt-1 truncate text-sm text-gray-500">
                          {activity.description}
                        </p>
                      </div>
                    </div>

                    <span className="whitespace-nowrap text-xs text-gray-400">
                      {formatActivityDate(activity.date)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-12 text-center text-gray-500">
                Nuk ka aktivitet të regjistruar.
              </div>
            )}
          </section>

          {/* SUMMARY */}
          <section className="rounded-2xl bg-gradient-to-br from-blue-700 to-indigo-900 p-5 text-white shadow-lg sm:p-6">
            <h2 className="text-lg font-bold">
              Përmbledhje
            </h2>

            <p className="mt-1 text-sm text-blue-100/80">
              Treguesit aktualë të biznesit
            </p>

            <div className="mt-6 space-y-5">
              <div>
                <div className="mb-2 flex justify-between text-sm">
                  <span>Porosi të përfunduara</span>
                  <span>{completionRate}%</span>
                </div>

                <div className="h-2 overflow-hidden rounded-full bg-white/20">
                  <div
                    className="h-full rounded-full bg-white transition-all duration-500"
                    style={{
                      width: `${Math.min(
                        completionRate,
                        100
                      )}%`,
                    }}
                  />
                </div>
              </div>

              <div>
                <div className="mb-2 flex justify-between text-sm">
                  <span>Produkte me stok të ulët</span>
                  <span>{lowStockProducts}</span>
                </div>

                <div className="h-2 overflow-hidden rounded-full bg-white/20">
                  <div
                    className="h-full rounded-full bg-orange-300 transition-all duration-500"
                    style={{
                      width: `${
                        realStats.products > 0
                          ? Math.min(
                              (lowStockProducts /
                                realStats.products) *
                                100,
                              100
                            )
                          : 0
                      }%`,
                    }}
                  />
                </div>
              </div>

              <div>
                <div className="mb-2 flex justify-between text-sm">
                  <span>Aktivitete totale</span>
                  <span>{totalActivities}</span>
                </div>

                <div className="h-2 overflow-hidden rounded-full bg-white/20">
                  <div className="h-full w-full rounded-full bg-green-300" />
                </div>
              </div>
            </div>

            <div className="mt-7 rounded-xl bg-white/10 p-4">
              <p className="text-sm text-blue-100">
                Stoku total
              </p>

              <p className="mt-1 text-3xl font-bold">
                {stockTotal}
              </p>

              <p className="mt-1 text-xs text-blue-100/70">
                njësi produktesh
              </p>
            </div>
          </section>
        </div>

        {/* ORDER STATUS */}
        {orderStatusData.length > 0 && (
          <section className="mt-6 rounded-2xl bg-white p-4 shadow-sm sm:p-6">
            <div className="mb-5">
              <h2 className="text-lg font-bold text-gray-900">
                Statusi i porosive
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                Numri real i porosive sipas statusit
              </p>
            </div>

            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={orderStatusData}
                  margin={{
                    top: 10,
                    right: 10,
                    left: -20,
                    bottom: 0,
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                  />

                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />

                  <Bar
                    dataKey="value"
                    name="Porosi"
                    fill="#16a34a"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default AdminAnalysis;